import Phaser from "phaser";
import { Player, type Origin } from "../entities/Player";
import { worldToIso, isoDepth, TILE_W, TILE_H } from "../core/iso";
import { DISTRICTS, CITY_GRID, type DistrictDef } from "../data/city";
import { GameState } from "../systems/GameState";
import { SaveManager } from "../core/SaveManager";
import { FLAGS } from "../config";
import { cityBuilding, tree, bench, streetlamp, fountain, car, crossing, busstop, house, civilian, marichei, sevillista, betico, bird, dog } from "../art/city";
import { Sfx } from "../systems/Sfx";
import { INTRO, BRIEFINGS, FAN_BANTER, FAN_TALKS } from "../data/story";
import { districtArt, introArt } from "../ui/illustrations";

const GRID = CITY_GRID;

function darken(hex: number, f: number): number {
  const r = Math.max(0, Math.min(255, Math.round(((hex >> 16) & 255) * f)));
  const g = Math.max(0, Math.min(255, Math.round(((hex >> 8) & 255) * f)));
  const b = Math.max(0, Math.min(255, Math.round((hex & 255) * f)));
  return (r << 16) | (g << 8) | b;
}
const lite = (hex: number, f: number) => Math.min(0xffffff, darken(hex, f)) & 0xffffff;

interface Building {
  def: DistrictDef;
  root: Phaser.GameObjects.Container;
  status: Phaser.GameObjects.Text;
}

/** La ciudad como HUB caminable (puntos 7 y 25). */
export class CityScene extends Phaser.Scene {
  private origin: Origin = { ox: 0, oy: 0 };
  private player!: Player;
  private buildings: Building[] = [];
  private cityProps: Phaser.GameObjects.Container[] = [];
  private nearest: Building | null = null;
  private floor?: Phaser.GameObjects.Graphics;
  private state!: GameState;

  private keyE!: Phaser.Input.Keyboard.Key;
  private keyUp!: Phaser.Input.Keyboard.Key;
  private keyDown!: Phaser.Input.Keyboard.Key;
  private keyLeft!: Phaser.Input.Keyboard.Key;
  private keyRight!: Phaser.Input.Keyboard.Key;
  private prompt!: Phaser.GameObjects.Container;
  private promptText!: Phaser.GameObjects.Text;
  private promptBg!: Phaser.GameObjects.Graphics;
  private promptKey!: Phaser.GameObjects.Text;
  // animación ambiental de la ciudad
  private animT = 0;
  private footT = 0;
  private footHi = false;
  private minimap?: Phaser.GameObjects.Container;
  private miniPlayer?: Phaser.GameObjects.Arc;
  private cars: { obj: Phaser.GameObjects.Container; x: number; y: number; vx: number }[] = [];
  private swayers: { obj: Phaser.GameObjects.Container; ph: number; amp: number }[] = [];
  private drops: { obj: Phaser.GameObjects.Arc; bx: number; by: number; ph: number; rise: number }[] = [];
  private backdrop?: Phaser.GameObjects.Graphics;
  private marichei?: { obj: Phaser.GameObjects.Container; x: number; y: number };
  private nearMarichei = false;
  private mariTip = 0;
  // aficionados (bético + sevillista) con charla ambiental
  private fans?: { x: number; y: number; betico: Phaser.GameObjects.Container; sevillista: Phaser.GameObjects.Container; tags: Phaser.GameObjects.Text[] };
  private nearFans = false;
  private fanTalk = 0;
  private fanBubble?: { root: Phaser.GameObjects.Container; bg: Phaser.GameObjects.Graphics; txt: Phaser.GameObjects.Text };
  private fanLine = 0;
  private fanTimer = 0;
  private peds: { obj: Phaser.GameObjects.Container; x: number; y: number; vx: number; vy: number; x0: number; x1: number; y0: number; y1: number; ph: number }[] = [];
  private birds: { obj: Phaser.GameObjects.Container; x: number; y: number; vx: number; ph: number }[] = [];
  private dog?: { obj: Phaser.GameObjects.Container; x: number; y: number; vx: number; x0: number; x1: number; ph: number };

  constructor() {
    super("city");
  }

  create(): void {
    this.state = new GameState();
    SaveManager.patch({ location: "city" });
    this.computeOrigin();
    this.buildBackdrop();
    this.drawFloor();
    this.buildCityProps();
    this.spawnBuildings();
    this.player = new Player(this, 8, 8);
    this.player.applyEquipment(this.state.equipment);
    this.player.sync(this.playerOrigin());
    this.setupCamera();
    this.buildMinimap();
    this.cameras.main.fadeIn(340, 20, 16, 10); // entrada con fundido

    const kb = this.input.keyboard!;
    this.keyUp = kb.addKey("W");
    this.keyDown = kb.addKey("S");
    this.keyLeft = kb.addKey("A");
    this.keyRight = kb.addKey("D");
    this.keyE = kb.addKey("E");
    kb.addKeys({ up: "UP", down: "DOWN", left: "LEFT", right: "RIGHT" });
    this.keyE.on("down", () => this.enter());

    this.add
      .text(16, 12, "La Ciudad · Mover: WASD/flechas · Entrar: E", {
        fontFamily: "Segoe UI, sans-serif",
        fontSize: "15px",
        color: "#f4ead0",
        backgroundColor: "#00000055",
        padding: { x: 8, y: 5 },
      })
      .setScrollFactor(0)
      .setDepth(100000);

    this.promptBg = this.add.graphics();
    this.promptKey = this.add.text(0, 0, "E", { fontFamily: "Segoe UI, sans-serif", fontSize: "13px", fontStyle: "bold", color: "#ffffff" }).setOrigin(0.5);
    this.promptText = this.add.text(0, 0, "", { fontFamily: "Segoe UI, sans-serif", fontSize: "14px", color: "#241d14" }).setOrigin(0, 0.5);
    this.prompt = this.add.container(0, 0, [this.promptBg, this.promptKey, this.promptText]).setDepth(90000).setVisible(false);

    this.scale.on("resize", () => {
      this.computeOrigin();
      this.buildBackdrop();
      this.drawFloor();
      this.buildCityProps();
      this.repositionBuildings();
      this.setupCamera();
      this.buildMinimap();
    });

    // historia introductoria la primera vez
    if (!this.state.hasFlag("intro_seen")) this.showIntro();
  }

  private computeOrigin(): void {
    this.origin = { ox: this.scale.width / 2 - (GRID.w - GRID.h) * (TILE_W / 4), oy: 100 };
  }

  /** Origen desplazado medio tile para que EPI se apoye en el CENTRO de la casilla (como iso()). */
  private playerOrigin(): Origin {
    return { ox: this.origin.ox, oy: this.origin.oy + TILE_H / 2 };
  }

  /** Cámara que sigue a EPI, con límites del recinto (para llegar a toda la ciudad). */
  private setupCamera(): void {
    const cam = this.cameras.main;
    const pts = [this.iso(0, 0), this.iso(GRID.w - 1, 0), this.iso(0, GRID.h - 1), this.iso(GRID.w - 1, GRID.h - 1)];
    const xs = pts.map((p) => p.sx), ys = pts.map((p) => p.sy);
    const minX = Math.min(...xs) - TILE_W * 1.5, maxX = Math.max(...xs) + TILE_W * 1.5;
    const minY = Math.min(...ys) - 230, maxY = Math.max(...ys) + TILE_H * 1.5;
    cam.setBounds(minX, minY, maxX - minX, maxY - minY);
    // Zoom adaptativo: con RESIZE el lienzo puede ser grande y el mundo (diseñado
    // para 1280×720) se veía diminuto. Escalamos para que la ciudad llene el marco,
    // con tope moderado para que EPI y edificios no salgan demasiado grandes.
    cam.setZoom(Phaser.Math.Clamp(Math.max(this.scale.width / 1280, this.scale.height / 720), 1, 1.15));
    cam.startFollow(this.player.container, true, 0.1, 0.1);
    cam.setDeadzone(200, 140);
  }

  /** Fondo de la ciudad: cielo, sol, montañas lejanas y nubes. */
  private buildBackdrop(): void {
    this.backdrop?.destroy();
    const W = this.scale.width, H = this.scale.height, over = 500;
    const g = this.add.graphics().setScrollFactor(0).setDepth(-19000);
    g.fillGradientStyle(0xbfe2ee, 0xbfe2ee, 0xe3f1e6, 0xe3f1e6, 1);
    g.fillRect(-over, -over, W + over * 2, H * 0.66 + over);
    g.fillStyle(0xfff6d8, 0.5); g.fillCircle(W * 0.82, H * 0.15, 42);
    const base = H * 0.44;
    g.fillStyle(0x9db7a9, 0.95);
    this.mountainRange(g, base + 34, [[-over, 40], [140, 120], [360, 76], [560, 150], [820, 96], [1080, 138], [W + over, 70]]);
    g.fillStyle(0x84a08f, 1);
    this.mountainRange(g, base + 56, [[-over, 70], [240, 176], [520, 108], [780, 196], [1050, 128], [W + over, 150]]);
    // caps de nieve
    g.fillStyle(0xeef4f0, 0.8);
    for (const [px, py] of [[240, base + 56 - 176], [780, base + 56 - 196]] as [number, number][]) g.fillTriangle(px, py, px - 22, py + 34, px + 22, py + 34);
    g.fillStyle(0xffffff, 0.72);
    for (const [cx, cy, s] of [[180, 92, 1], [520, 60, 1.3], [900, 108, 0.9], [1160, 72, 1.1]] as [number, number, number][]) { g.fillEllipse(cx, cy, 60 * s, 20 * s); g.fillEllipse(cx + 32 * s, cy + 6, 44 * s, 16 * s); g.fillEllipse(cx - 28 * s, cy + 6, 40 * s, 15 * s); }
    this.backdrop = g;
  }

  private mountainRange(g: Phaser.GameObjects.Graphics, baseY: number, peaks: [number, number][]): void {
    g.beginPath();
    g.moveTo(peaks[0][0], baseY);
    for (const [px, ph] of peaks) g.lineTo(px, baseY - ph);
    g.lineTo(peaks[peaks.length - 1][0], baseY);
    g.closePath();
    g.fillPath();
  }

  /**
   * Punto de apoyo (pies) para lo que se PLANTA en un tile: el CENTRO del rombo,
   * no su vértice superior. Así `(x,y)` = tile real de suelo y los props/edificios
   * quedan sobre su casilla (antes caían medio tile arriba-izquierda, encima de la
   * calzada). El suelo se dibuja restando este medio tile en drawFloor.
   */
  private iso(x: number, y: number): { sx: number; sy: number } {
    const p = worldToIso(x, y);
    return { sx: this.origin.ox + p.sx, sy: this.origin.oy + p.sy + TILE_H / 2 };
  }

  /** Trama urbana: calles, parques o plaza según el tile. */
  private cityKind(x: number, y: number): "road" | "park" | "plaza" {
    const horiz = Math.abs(y - 4.5) < 0.55 || Math.abs(y - 9) < 0.45;
    const vert = Math.abs(x - 5.5) < 0.55 || Math.abs(x - 9.5) < 0.55;
    if (horiz || vert) return "road";
    const inRect = (x0: number, x1: number, y0: number, y1: number) => x >= x0 && x <= x1 && y >= y0 && y <= y1;
    if (inRect(0.5, 2.5, 5.5, 7.5) || inRect(11.5, 13.5, 4, 5.5) || inRect(0.5, 4.3, 9.8, 11.5)) return "park";
    return "plaza";
  }

  /** ¿En qué dirección corre la calle en este tile? (para las marcas viales) */
  private roadDir(x: number, y: number): "x" | "y" | "cross" {
    const h = Math.abs(y - 4.5) < 0.55 || Math.abs(y - 9) < 0.45;
    const v = Math.abs(x - 5.5) < 0.55 || Math.abs(x - 9.5) < 0.55;
    return h && v ? "cross" : h ? "x" : "y";
  }

  private drawFloor(): void {
    this.floor?.destroy();
    this.cameras.main.setBackgroundColor(0xb2ac9c); // pavimento (nunca vacío oscuro)
    const g = this.add.graphics().setDepth(-10000);
    const HW = TILE_W / 2, HH = TILE_H / 2;
    for (let x = 0; x < GRID.w; x++) {
      for (let y = 0; y < GRID.h; y++) {
        const c = this.iso(x, y);
        const sx = c.sx, sy = c.sy - HH; // sy = vértice superior del rombo (iso ya da el centro)
        const cy = c.sy;                 // centro del tile
        const h = (x * 928371 + y * 12347) & 255;
        const kind = this.cityKind(x, y);
        const alt = (x + y) % 2 === 0;
        let base: number, stroke: number, sa: number;
        if (kind === "road") { base = alt ? 0x45454b : 0x4a4a50; stroke = 0x35353a; sa = 0.3; }
        else if (kind === "park") { base = alt ? 0x679350 : 0x6f9a52; stroke = 0x4f7a3e; sa = 0.25; }
        else { base = alt ? 0xc6c1b3 : 0xc0bbac; stroke = 0xa89f8c; sa = 0.16; }
        g.fillStyle(h % 9 === 0 ? darken(base, 0.94) : base, 1).lineStyle(1, stroke, sa);
        g.beginPath();
        g.moveTo(sx, sy); g.lineTo(sx + HW, sy + HH); g.lineTo(sx, sy + TILE_H); g.lineTo(sx - HW, sy + HH); g.closePath();
        g.fillPath(); g.strokePath();
        if (kind === "road") {
          const dir = this.roadDir(x, y);
          // marca central discontinua
          if (dir !== "cross" && (Math.round(dir === "x" ? x : y) % 2 === 0)) {
            g.lineStyle(2.5, 0xd8b93f, 0.85);
            if (dir === "x") { g.beginPath(); g.moveTo(sx - HW * 0.32, cy - HH * 0.32); g.lineTo(sx + HW * 0.32, cy + HH * 0.32); g.strokePath(); }
            else { g.beginPath(); g.moveTo(sx + HW * 0.32, cy - HH * 0.32); g.lineTo(sx - HW * 0.32, cy + HH * 0.32); g.strokePath(); }
          }
        } else if (kind === "park") {
          // matas de césped
          g.fillStyle(darken(base, 0.85), 0.5);
          for (let k = 0; k < 3; k++) g.fillCircle(sx - 14 + ((h >> k) % 28), cy - 6 + ((h >> (k + 1)) % 14), 1.4);
          g.fillStyle(lite(base, 1.08), 0.4); g.fillCircle(sx - 6, cy - 4, 2);
        } else if (h % 5 === 0) {
          g.fillStyle(0xa89f8c, 0.22); g.fillCircle(sx - 8 + (h % 16), cy + ((h >> 2) % 10) - 4, 1.4);
        }
      }
    }
    this.floor = g;
  }

  private buildCityProps(): void {
    this.cityProps.forEach((p) => p.destroy());
    this.cityProps = [];
    const place = (obj: Phaser.GameObjects.Container, tx: number, ty: number) => {
      const { sx, sy } = this.iso(tx, ty);
      obj.setPosition(sx, sy).setDepth(isoDepth(tx, ty));
      this.cityProps.push(obj);
    };
    // decals planos (paso de cebra): bajo personajes y edificios
    const placeFlat = (obj: Phaser.GameObjects.Container, tx: number, ty: number) => {
      const { sx, sy } = this.iso(tx, ty); // iso ya devuelve el centro del tile
      obj.setPosition(sx, sy).setDepth(-9985);
      this.cityProps.push(obj);
    };
    placeFlat(crossing(this, "x"), 4.4, 4.5); placeFlat(crossing(this, "x"), 8.4, 9);
    placeFlat(crossing(this, "y"), 5.5, 6); placeFlat(crossing(this, "y"), 9.5, 6);

    // fuente + chorro de agua animado
    place(fountain(this), 1.5, 6.4);
    this.drops = [];
    const fp = this.iso(1.5, 6.4);
    for (let i = 0; i < 5; i++) {
      const d = this.add.circle(fp.sx, fp.sy - 30, 2, 0xbfe0e6, 0.8).setDepth(isoDepth(1.5, 6.4) + 2);
      this.cityProps.push(d as unknown as Phaser.GameObjects.Container);
      this.drops.push({ obj: d, bx: fp.sx, by: fp.sy - 30, ph: i / 5, rise: 18 });
    }
    // árboles (se mecen)
    this.swayers = [];
    for (const [x, y] of [[0.7, 5.6], [2.2, 5.6], [0.7, 7.4], [2.2, 7.4], [1.4, 6.6], [12.6, 3.2], [13.3, 5.6], [11.2, 3.2], [4.3, 3.2], [7.2, 3.2], [12.7, 8.0], [4.2, 8.0], [2, 2.8], [6.7, 7], [10.7, 6.7], [1.5, 11.1], [3.6, 11.1], [4.2, 10.0], [0.8, 9.9]] as [number, number][]) {
      const t = tree(this); const p = this.iso(x, y); t.setPosition(p.sx, p.sy).setDepth(isoDepth(x, y));
      this.cityProps.push(t); this.swayers.push({ obj: t, ph: x + y, amp: 1.1 });
    }
    for (const [x, y] of [[1.5, 7.5], [12.5, 5.4], [6.6, 6.4], [2.6, 10.9]] as [number, number][]) place(bench(this), x, y);
    for (const [x, y] of [[4.5, 3.5], [6.3, 3.5], [4.5, 8.2], [10.4, 8.2], [2.8, 3.5], [8.5, 9.7], [6.3, 5.3], [10.4, 6.6]] as [number, number][]) place(streetlamp(this), x, y);
    // coches CIRCULANDO por las avenidas horizontales (morro +x)
    this.cars = [];
    for (const [x, y, col, vx] of [[3, 4.5, 0x3a6ea0, 0.9], [10, 4.5, 0xd8b93f, 1.25], [1.5, 9, 0xb23a2a, 0.8], [8, 9, 0x5f676d, 1.05]] as [number, number, number, number][]) {
      const c = car(this, col); const p = this.iso(x, y); c.setPosition(p.sx, p.sy).setDepth(isoDepth(x, y));
      this.cityProps.push(c); this.cars.push({ obj: c, x, y, vx });
    }
    place(busstop(this), 4.2, 3.5);

    // casas de las afueras (dan forma de pueblo)
    for (const [x, y, col, roof] of [[1, 1, 0xd8b98a, 0xa85a44], [2.4, 1, 0xcfc0a0, 0x8a6d4a], [6.8, 1.2, 0xd8c4a0, 0xa85a44], [0.7, 3, 0xc9b48a, 0x7a5a3a], [0.7, 10.4, 0xd8b98a, 0x9a5040], [2.3, 10.8, 0xcfc0a0, 0xa85a44], [11.3, 10.8, 0xd8c4a0, 0x8a6d4a], [13.6, 7.9, 0xc9b48a, 0xa85a44]] as [number, number, number, number][]) place(house(this, col, roof), x, y);

    // Marichei, la guía de orientación (interactiva)
    const mx = 4.4, my = 6.6;
    const mObj = marichei(this); const mp = this.iso(mx, my); mObj.setPosition(mp.sx, mp.sy).setDepth(isoDepth(mx, my));
    this.cityProps.push(mObj); this.marichei = { obj: mObj, x: mx, y: my };
    // cartelito flotante para que se la reconozca
    const tag = this.add.text(mp.sx, mp.sy - 62, "Marichei", { fontFamily: "Segoe UI, sans-serif", fontSize: "10px", color: "#fff", fontStyle: "bold", backgroundColor: "#df5f2a", padding: { x: 5, y: 1 } }).setOrigin(0.5, 1).setDepth(isoDepth(mx, my) + 1);
    this.cityProps.push(tag as unknown as Phaser.GameObjects.Container);

    // los aficionados (bético + sevillista) charlando en un banco
    place(bench(this), 6.7, 7.7);
    const fx = 6.7, fy = 7.4;
    const bt = betico(this); const bp = this.iso(6.3, fy); bt.setPosition(bp.sx, bp.sy).setDepth(isoDepth(6.3, fy));
    const sv = sevillista(this); const sp = this.iso(7.1, fy); sv.setPosition(sp.sx, sp.sy).setDepth(isoDepth(7.1, fy));
    this.cityProps.push(bt, sv);
    const nameTag = (name: string, color: string, px: number, py: number): Phaser.GameObjects.Text =>
      this.add.text(px, py - 52, name, { fontFamily: "Segoe UI, sans-serif", fontSize: "10px", color: "#fff", fontStyle: "bold", backgroundColor: color, padding: { x: 5, y: 1 } }).setOrigin(0.5, 1).setDepth(94000);
    const tagM = nameTag("Manolo", "#0b7a3b", bp.sx, bp.sy);
    const tagP = nameTag("Paco", "#c4122e", sp.sx, sp.sy);
    this.cityProps.push(tagM as unknown as Phaser.GameObjects.Container, tagP as unknown as Phaser.GameObjects.Container);
    this.fans = { x: fx, y: fy, betico: bt, sevillista: sv, tags: [tagM, tagP] };
    // bocadillo de charla ambiental (oculto por defecto)
    const bubTxt = this.add.text(0, 0, "", { fontFamily: "Segoe UI, sans-serif", fontSize: "14px", color: "#241d14", wordWrap: { width: 210 }, align: "center" }).setOrigin(0.5, 1);
    const bubBg = this.add.graphics();
    const bubRoot = this.add.container(0, 0, [bubBg, bubTxt]).setDepth(95000).setVisible(false);
    this.cityProps.push(bubRoot);
    this.fanBubble = { root: bubRoot, bg: bubBg, txt: bubTxt };
    this.fanLine = 0; this.fanTimer = 0;

    // vecinos paseando (van y vienen por aceras/plaza)
    this.peds = [];
    const pedSpecs: [number, number, number, number, number, number, number][] = [
      // x, y, vx, vy, min, max, shirt
      [2.2, 6.6, 0.7, 0, 2.2, 4.4, 0x3a6ea0],
      [1.2, 6.6, 0, 0.6, 5.4, 8.0, 0x7a5a86],
      [7, 6, 0, 0.7, 5.4, 8.0, 0x2f8f57],
      [10.8, 7.6, 0.6, 0, 10.5, 12.4, 0xb23a2a],
      [7, 2.8, 0.8, 0, 6.4, 8.4, 0xd8a800],
    ];
    // aspectos diversos (piel + pelo) para que los vecinos no sean clónicos
    const pedLooks: [number, number][] = [[0xf2d1b3, 0x2a1e14], [0xd49a6a, 0x14100c], [0x8d5a34, 0x120d08], [0xe8b48c, 0x8f8f8f], [0x6b4226, 0x100c08], [0xefc199, 0x9a3b1e]];
    let pi = 0;
    for (const [x, y, vx, vy, lo, hi, shirt] of pedSpecs) {
      const [skin, hair] = pedLooks[pi++ % pedLooks.length];
      const c = civilian(this, shirt, 0x2b2f36, hair, skin); const p = this.iso(x, y); c.setPosition(p.sx, p.sy).setDepth(isoDepth(x, y));
      this.cityProps.push(c);
      this.peds.push({ obj: c, x, y, vx, vy, x0: vx ? lo : x, x1: vx ? hi : x, y0: vy ? lo : y, y1: vy ? hi : y, ph: x + y });
    }

    // perro paseando por la plaza
    const dg = dog(this); const dp = this.iso(3.4, 7.6); dg.setPosition(dp.sx, dp.sy).setDepth(isoDepth(3.4, 7.6));
    this.cityProps.push(dg);
    this.dog = { obj: dg, x: 3.4, y: 7.6, vx: 0.9, x0: 2.4, x1: 4.4, ph: 2 };

    // pájaros volando por el cielo (fijos a la cámara)
    this.birds = [];
    for (let i = 0; i < 5; i++) {
      const bx = 120 + i * 250, by = 66 + (i % 3) * 32;
      const bd = bird(this); bd.setScrollFactor(0).setPosition(bx, by).setDepth(-18800);
      this.cityProps.push(bd);
      this.birds.push({ obj: bd, x: bx, y: by, vx: 16 + i * 4, ph: i * 1.3 });
    }
  }

  private enterable(def: DistrictDef): boolean {
    return !!def.sceneKey && !!this.scene.get(def.sceneKey) && (!def.requires || this.state.chapter(def.requires).done);
  }

  private spawnBuildings(): void {
    this.buildings.forEach((b) => b.root.destroy());
    this.buildings = [];
    for (const def of DISTRICTS) {
      const done = this.state.chapter(def.id).done;
      const sec = this.state.security[def.id];
      const canEnter = this.enterable(def);
      const building = cityBuilding(this, def.id, def.color);
      const name = this.add
        .text(0, -112, def.name, { fontFamily: "Segoe UI, sans-serif", fontSize: "11px", color: "#f4ead0", fontStyle: "bold", backgroundColor: "#241d14", padding: { x: 6, y: 2 } })
        .setOrigin(0.5, 1);
      let statusStr: string;
      let statusColor: string;
      if (!def.sceneKey) { statusStr = "Próximamente"; statusColor = "#b9b0a0"; }
      else if (done) { statusStr = `Resuelto · ${sec ?? ""}%`; statusColor = "#7fe0a6"; }
      else if (canEnter) { statusStr = "Disponible"; statusColor = "#f0b070"; }
      else { statusStr = "Bloqueado"; statusColor = "#e08a7a"; }
      const status = this.add
        .text(0, -96, statusStr, { fontFamily: "Segoe UI, sans-serif", fontSize: "9px", color: statusColor, backgroundColor: "#241d14cc", padding: { x: 5, y: 1 } })
        .setOrigin(0.5, 1);
      const { sx, sy } = this.iso(def.pos.x, def.pos.y);
      const root = this.add.container(sx, sy, [building, name, status]).setDepth(isoDepth(def.pos.x, def.pos.y));
      this.buildings.push({ def, root, status });
    }
  }

  private repositionBuildings(): void {
    for (const b of this.buildings) {
      const { sx, sy } = this.iso(b.def.pos.x, b.def.pos.y);
      b.root.setPosition(sx, sy);
    }
  }

  private enter(): void {
    if (this.nearFans) { this.showFans(); return; }
    if (this.nearMarichei) { this.showMarichei(); return; }
    if (!this.nearest) return;
    const def = this.nearest.def;
    if (this.enterable(def)) {
      // Feature flag: Obras del Norte abre la microescena nueva (Tiled + sprites).
      const target = FLAGS.USE_NEW_WORKS_SCENE && def.sceneKey === "obras" ? "obras-micro" : def.sceneKey!;
      this.showBriefing(def, target); // briefing "qué ha pasado" antes de entrar
    } else if (def.sceneKey && def.requires) {
      const req = DISTRICTS.find((d) => d.id === def.requires);
      this.toast(`Antes resuelve ${req?.name ?? def.requires}`);
    } else {
      this.toast(`${def.name}: próximamente`);
    }
  }

  private toast(msg: string): void {
    const t = this.add
      .text(this.scale.width / 2, 70, msg, {
        fontFamily: "Segoe UI, sans-serif",
        fontSize: "16px",
        color: "#241d14",
        backgroundColor: "#f0a91b",
        padding: { x: 12, y: 7 },
      })
      .setOrigin(0.5)
      .setScrollFactor(0)
      .setDepth(100002);
    this.tweens.add({ targets: t, alpha: 0, y: 52, delay: 1200, duration: 500, onComplete: () => t.destroy() });
  }

  private static readonly MARI_TIPS = [
    "Antes de tocar nada, <b>observa</b>. Casi todos los accidentes avisan: una señal fuera de sitio, un cable pelado, una prisa. Mira primero.",
    "Recuerda la <b>jerarquía</b>: primero eliminar el peligro, luego proteger a todos, y el EPI es el último recurso, nunca el primero.",
    "En cada lugar, <b>habla con la gente</b>. Quien trabaja allí sabe lo que pasó; solo hay que escuchar sin juzgar.",
    "Un riesgo bien identificado ya está medio resuelto. Pregúntate siempre: <b>¿qué puede pasar aquí y a quién?</b>",
    "La brújula no te dice a dónde ir, te ayuda a <b>no perderte</b>. Ve caso por caso: empieza por Obras del Norte; el resto se abre al avanzar.",
    "<b>Mide antes de suponer</b>: el sonómetro, el verificador, el luxómetro... los números no discuten.",
    "Si algo te parece 'normal pero raro', anótalo en el <b>cuaderno</b> (tecla J). Los detalles pequeños resuelven los casos.",
    "<b>Equípate según el trabajo y el entorno</b>, ni de más ni de menos. En una oficina no hace falta casco; en una obra, sí.",
    "La seguridad no va de miedo, va de <b>cuidado</b>: cuidarte tú y cuidar a los demás. Ese es todo el secreto.",
  ];

  /** Construye la estructura de un modal en DOM (fondo, tarjeta, cabecera, cuerpo, pie). */
  private modalCard(id: string, headText: string, headColor = "#df5f2a"): {
    backdrop: HTMLDivElement; body: HTMLDivElement; foot: HTMLDivElement; mkBtn: (t: string, p?: boolean) => HTMLButtonElement; close: () => void;
  } {
    const backdrop = document.createElement("div");
    backdrop.id = id;
    backdrop.style.cssText = "position:fixed;inset:0;background:rgba(30,20,8,.55);display:flex;align-items:center;justify-content:center;z-index:60;font-family:'Segoe UI',system-ui,sans-serif;padding:16px";
    const card = document.createElement("div");
    card.style.cssText = "background:#efe3cb;border:2px solid #8a785a;border-radius:14px;max-width:480px;width:100%;box-shadow:0 18px 50px rgba(0,0,0,.5);overflow:hidden";
    const head = document.createElement("div");
    head.style.cssText = `padding:.7rem 1rem;background:${headColor};color:#fff;font-weight:800;letter-spacing:.02em`;
    head.textContent = headText;
    const body = document.createElement("div");
    body.style.cssText = "padding:1.1rem 1.2rem;color:#241d14;line-height:1.62;font-size:1.12rem";
    const foot = document.createElement("div");
    foot.style.cssText = "padding:.7rem 1rem;background:#e6d7ba;display:flex;gap:.5rem;justify-content:flex-end;align-items:center";
    const mkBtn = (text: string, primary = false): HTMLButtonElement => {
      const b = document.createElement("button");
      b.textContent = text;
      b.style.cssText = `padding:.5em 1.05em;border-radius:11px;border:2px solid #8a785a;background:${primary ? "linear-gradient(#f6b62e,#f0a91b)" : "#f6eeda"};color:#241d14;font-weight:700;cursor:pointer;font-family:inherit`;
      return b;
    };
    card.append(head, body, foot); backdrop.appendChild(card); document.body.appendChild(backdrop);
    const close = (): void => backdrop.remove();
    return { backdrop, body, foot, mkBtn, close };
  }

  /** Marichei da un consejo de orientación. */
  private showMarichei(): void {
    if (document.getElementById("mari-modal")) return;
    Sfx.talk();
    const m = this.modalCard("mari-modal", "Marichei · tu guía de orientación");
    const p = document.createElement("p"); p.style.margin = "0"; m.body.appendChild(p);
    const setTip = (): void => { p.innerHTML = `<b style="color:#c24a1a">Consejo:</b> ${CityScene.MARI_TIPS[this.mariTip % CityScene.MARI_TIPS.length]}`; };
    setTip();
    const another = m.mkBtn("Otro consejo"); another.onclick = () => { this.mariTip++; setTip(); Sfx.click(); };
    const story = m.mkBtn("Ver la historia"); story.onclick = () => { m.close(); this.showIntro(true); };
    const close = m.mkBtn("Gracias", true); close.onclick = () => { m.close(); Sfx.click(); };
    m.foot.append(story, another, close);
    m.backdrop.addEventListener("click", (e) => { if (e.target === m.backdrop) m.close(); });
  }

  /** Historia introductoria (paneles con Siguiente/Saltar). Se ve una vez. */
  private showIntro(replay = false): void {
    if (document.getElementById("intro-modal")) return;
    Sfx.talk();
    const m = this.modalCard("intro-modal", "Las Aventuras de EPI", "#241d14");
    let idx = 0;
    const illus = document.createElement("div"); illus.style.cssText = "margin:-1.1rem -1.2rem 1rem;border-bottom:2px solid #cbb891;overflow:hidden;line-height:0"; m.body.appendChild(illus);
    const p = document.createElement("p"); p.style.margin = "0"; m.body.appendChild(p);
    const dots = document.createElement("span"); dots.style.cssText = "margin-right:auto;display:flex;gap:6px"; m.foot.appendChild(dots);
    const skip = m.mkBtn("Saltar"); const next = m.mkBtn("Siguiente", true);
    m.foot.append(skip, next);
    const finish = (): void => { if (!replay) this.state.setFlag("intro_seen"); m.close(); };
    const render = (): void => {
      const panel = INTRO[idx];
      illus.innerHTML = introArt(idx);
      p.innerHTML = `<b style="display:block;font-size:1.05rem;margin-bottom:.35rem;color:#c24a1a">${panel.title}</b>${panel.body}`;
      next.textContent = idx === INTRO.length - 1 ? "¡Vamos!" : "Siguiente";
      dots.innerHTML = INTRO.map((_, i) => `<i style="width:8px;height:8px;border-radius:50%;display:inline-block;background:${i === idx ? "#df5f2a" : "#c9b78f"}"></i>`).join("");
    };
    skip.onclick = () => { finish(); Sfx.click(); };
    next.onclick = () => { if (idx < INTRO.length - 1) { idx++; render(); Sfx.click(); } else { finish(); Sfx.good(); } };
    render();
  }

  private static readonly MINI = { W: 152, H: 116, pad: 12, top: 12 };
  private miniX(wx: number): number { const m = CityScene.MINI; return m.pad + (wx / (GRID.w - 1)) * (m.W - m.pad * 2); }
  private miniY(wy: number): number { const m = CityScene.MINI; return m.pad + 12 + (wy / (GRID.h - 1)) * (m.H - m.pad * 2 - 12); }

  /** Color del distrito en el minimapa según su estado. */
  private districtStatus(d: DistrictDef): number {
    if (!d.sceneKey) return 0xb9b0a0;                          // próximamente (gris)
    if (this.state.chapter(d.id).done) return 0x2f8f57;        // resuelto (verde)
    const canEnter = !d.requires || this.state.chapter(d.requires).done;
    return canEnter ? 0xf0b070 : 0xe08a7a;                     // disponible / bloqueado
  }

  /** Minimapa de la ciudad (esquina superior derecha): distritos por estado + EPI. */
  private buildMinimap(): void {
    this.minimap?.destroy();
    const m = CityScene.MINI;
    const g = this.add.graphics();
    g.fillStyle(0x241d14, 0.74); g.fillRoundedRect(0, 0, m.W, m.H, 8);
    g.lineStyle(1, 0x8a785a, 0.85); g.strokeRoundedRect(0, 0, m.W, m.H, 8);
    const kids: Phaser.GameObjects.GameObject[] = [g];
    kids.push(this.add.text(m.pad, 5, "MAPA", { fontFamily: "Segoe UI, sans-serif", fontSize: "9px", color: "#c9b78f", fontStyle: "bold" }).setOrigin(0, 0));
    for (const d of DISTRICTS) {
      kids.push(this.add.circle(this.miniX(d.pos.x), this.miniY(d.pos.y), 4, this.districtStatus(d)).setStrokeStyle(1, 0x14100a, 0.6));
    }
    this.miniPlayer = this.add.circle(this.miniX(8), this.miniY(8), 3.5, 0xdf5f2a).setStrokeStyle(1.5, 0xffffff, 0.9);
    kids.push(this.miniPlayer);
    this.minimap = this.add.container(this.scale.width - m.W - 12, m.top, kids).setScrollFactor(0).setDepth(100001);
  }

  private updateMinimap(): void {
    this.miniPlayer?.setPosition(this.miniX(this.player.x), this.miniY(this.player.y));
  }

  /** Entra a un distrito con fundido a negro (transición suave). */
  private enterDistrict(target: string): void {
    const cam = this.cameras.main;
    cam.fadeOut(280, 20, 16, 10);
    cam.once("camerafadeoutcomplete", () => this.scene.start(target));
  }

  /** Briefing "qué ha pasado aquí" antes de entrar a un distrito. */
  private showBriefing(def: DistrictDef, target: string): void {
    const b = BRIEFINGS[def.id];
    if (!b) { this.enterDistrict(target); return; }
    Sfx.talk();
    const m = this.modalCard("brief-modal", `Caso: ${b.title}`);
    const art = districtArt(def.id);
    m.body.innerHTML = `${art ? `<div style="margin:-1.1rem -1.2rem 1rem;border-bottom:2px solid #cbb891;overflow:hidden;line-height:0">${art}</div>` : ""}${b.body}<p style="margin:.7rem 0 0;color:#5c5140;font-size:.9rem">Habla con la gente, observa, mide y actúa. No preguntes qué harías: hazlo.</p>`;
    const back = m.mkBtn("Volver"); back.onclick = () => { m.close(); Sfx.click(); };
    const go = m.mkBtn("Entrar a investigar", true); go.onclick = () => { m.close(); Sfx.pickup(); this.enterDistrict(target); };
    m.foot.append(back, go);
    m.backdrop.addEventListener("click", (e) => { if (e.target === m.backdrop) m.close(); });
  }

  update(_time: number, delta: number): void {
    const dt = delta / 1000;
    const kb = this.input.keyboard!;
    const up = this.keyUp.isDown || kb.keys[Phaser.Input.Keyboard.KeyCodes.UP]?.isDown;
    const down = this.keyDown.isDown || kb.keys[Phaser.Input.Keyboard.KeyCodes.DOWN]?.isDown;
    const left = this.keyLeft.isDown || kb.keys[Phaser.Input.Keyboard.KeyCodes.LEFT]?.isDown;
    const right = this.keyRight.isDown || kb.keys[Phaser.Input.Keyboard.KeyCodes.RIGHT]?.isDown;
    let dx = 0;
    let dy = 0;
    if (up) { dx -= 1; dy -= 1; }
    if (down) { dx += 1; dy += 1; }
    if (left) { dx -= 1; dy += 1; }
    if (right) { dx += 1; dy -= 1; }
    if (dx !== 0 || dy !== 0) {
      this.player.move(dx, dy, dt, GRID);
      this.footT += delta;
      if (this.footT >= 290) { this.footT = 0; this.footHi = !this.footHi; Sfx.foot(this.footHi); }
    } else {
      this.footT = 290;
    }
    this.player.sync(this.playerOrigin());
    this.updateProximity();
    this.updateMinimap();

    // vida de la ciudad
    this.animT += delta;
    for (const c of this.cars) {
      c.x += c.vx * dt;
      if (c.x > GRID.w + 1.5) c.x = -1.5;
      const p = this.iso(c.x, c.y);
      c.obj.setPosition(p.sx, p.sy).setDepth(isoDepth(c.x, c.y));
    }
    for (const s of this.swayers) { if (!s.obj.active) continue; s.obj.setAngle(Math.sin(this.animT * 0.0014 + s.ph) * s.amp); }
    for (const d of this.drops) {
      if (!d.obj.active) continue;
      const cyc = (((this.animT * 0.0024 + d.ph) % 1) + 1) % 1;
      const up = Math.sin(cyc * Math.PI);
      d.obj.y = d.by - up * d.rise;
      d.obj.x = d.bx + (cyc - 0.5) * 12 * (d.ph < 0.5 ? 1 : -1);
      d.obj.setAlpha(0.35 + up * 0.55);
    }
    // vecinos paseando (van y vienen, con balanceo de paso)
    for (const pd of this.peds) {
      pd.x += pd.vx * dt; pd.y += pd.vy * dt;
      if (pd.vx && (pd.x < pd.x0 || pd.x > pd.x1)) pd.vx *= -1;
      if (pd.vy && (pd.y < pd.y0 || pd.y > pd.y1)) pd.vy *= -1;
      const p = this.iso(pd.x, pd.y);
      pd.obj.setPosition(p.sx, p.sy + Math.sin(this.animT * 0.012 + pd.ph) * 1.5).setDepth(isoDepth(pd.x, pd.y));
    }
    // pájaros volando (aleteo con scaleY) y perro paseando
    for (const bd of this.birds) {
      bd.x += bd.vx * dt;
      if (bd.x > this.scale.width + 40) bd.x = -40;
      bd.obj.setPosition(bd.x, bd.y + Math.sin(this.animT * 0.004 + bd.ph) * 6);
      bd.obj.setScale(1, 0.6 + 0.4 * Math.abs(Math.sin(this.animT * 0.02 + bd.ph)));
    }
    if (this.dog) {
      this.dog.x += this.dog.vx * dt;
      if (this.dog.x < this.dog.x0 || this.dog.x > this.dog.x1) this.dog.vx *= -1;
      const p = this.iso(this.dog.x, this.dog.y);
      this.dog.obj.setPosition(p.sx, p.sy + Math.sin(this.animT * 0.016 + this.dog.ph) * 1).setDepth(isoDepth(this.dog.x, this.dog.y));
    }

    // charla ambiental de los aficionados (bocadillo que cicla al acercarte)
    if (this.fans && this.fanBubble) {
      if (this.nearFans) {
        this.fanTimer += delta;
        if (this.fanTimer > 3400) { this.fanTimer = 0; this.fanLine = (this.fanLine + 1) % FAN_BANTER.length; }
        const line = FAN_BANTER[this.fanLine];
        const sp = line.who === "b" ? this.fans.betico : this.fans.sevillista;
        this.drawFanBubble(line.text, sp.x, sp.y - 36);
        this.fanBubble.root.setVisible(true);
        this.fans.tags.forEach((t) => t.setVisible(false));
      } else if (this.fanBubble.root.visible) {
        this.fanBubble.root.setVisible(false);
        this.fans.tags.forEach((t) => t.setVisible(true));
        this.fanLine = 0; this.fanTimer = 0;
      }
    }
  }

  /** Dibuja el bocadillo de charla apuntando a un personaje. */
  private drawFanBubble(text: string, x: number, y: number): void {
    const b = this.fanBubble!;
    b.txt.setText(text);
    const th = b.txt.height, tw = b.txt.width;
    const w = tw + 20, h = th + 14, top = -(h + 8);
    b.txt.setPosition(0, top + 7 + th);
    const g = b.bg; g.clear();
    g.fillStyle(0x1a1206, 0.18); g.fillRoundedRect(-w / 2 + 2, top + 3, w, h, 8);
    g.fillStyle(0xffffff, 0.98); g.fillRoundedRect(-w / 2, top, w, h, 8);
    g.lineStyle(1.5, 0xdcd3c2, 1); g.strokeRoundedRect(-w / 2, top, w, h, 8);
    g.fillStyle(0xffffff, 0.98); g.beginPath(); g.moveTo(-6, top + h - 1); g.lineTo(6, top + h - 1); g.lineTo(0, top + h + 8); g.closePath(); g.fillPath();
    b.root.setPosition(x, y);
  }

  /** Conversación con los aficionados (piqué + batallita de seguridad). */
  private showFans(): void {
    if (document.getElementById("fans-modal")) return;
    Sfx.talk();
    const talk = FAN_TALKS[this.fanTalk % FAN_TALKS.length];
    const m = this.modalCard("fans-modal", "En la plaza · béticos y sevillistas", "#0b7a3b");
    const label = (who: "e" | "b" | "s"): [string, string] => who === "b" ? ["Manolo (Betis)", "#0b7a3b"] : who === "s" ? ["Paco (Sevilla)", "#c4122e"] : ["EPI", "#df5f2a"];
    m.body.innerHTML = talk.map((l) => { const [nm, col] = label(l.who); return `<p style="margin:.15rem 0 .55rem"><b style="color:${col}">${nm}:</b> ${l.text}</p>`; }).join("");
    const other = m.mkBtn("Otra batallita");
    other.onclick = () => { this.fanTalk = (this.fanTalk + 1) % FAN_TALKS.length; m.close(); this.showFans(); };
    const close = m.mkBtn("Hasta luego", true);
    close.onclick = () => { m.close(); Sfx.click(); };
    m.foot.append(other, close);
    m.backdrop.addEventListener("click", (e) => { if (e.target === m.backdrop) m.close(); });
  }

  private updateProximity(): void {
    let best: Building | null = null;
    let bestD = Infinity;
    for (const b of this.buildings) {
      const d = Math.hypot(b.def.pos.x - this.player.x, b.def.pos.y - this.player.y);
      if (d <= 1.8 && d < bestD) {
        best = b;
        bestD = d;
      }
    }
    // personajes con prioridad si estás a su lado
    this.nearMarichei = false; this.nearFans = false;
    if (this.marichei) {
      const dm = Math.hypot(this.marichei.x - this.player.x, this.marichei.y - this.player.y);
      if (dm <= 1.6 && dm < bestD) { this.nearMarichei = true; best = null; bestD = dm; }
    }
    if (this.fans) {
      const df = Math.hypot(this.fans.x - this.player.x, this.fans.y - this.player.y);
      if (df <= 1.7 && df < bestD) { this.nearFans = true; this.nearMarichei = false; best = null; }
    }
    this.nearest = best;
    if (this.nearFans) {
      this.promptText.setText("Hablar con los aficionados");
      this.layoutPrompt();
      this.prompt.setPosition(this.player.container.x, this.player.container.y - 96).setVisible(true);
    } else if (this.nearMarichei) {
      this.promptText.setText("Hablar con Marichei");
      this.layoutPrompt();
      this.prompt.setPosition(this.player.container.x, this.player.container.y - 96).setVisible(true);
    } else if (best) {
      const canEnter = this.enterable(best.def);
      this.promptText.setText(canEnter ? `Entrar · ${best.def.name}` : best.def.name);
      this.layoutPrompt();
      this.prompt.setPosition(this.player.container.x, this.player.container.y - 96).setVisible(true);
    } else {
      this.prompt.setVisible(false);
    }
  }

  /** Bocadillo de acción: tarjeta blanca + tecla E (keycap), igual que en los capítulos. */
  private layoutPrompt(): void {
    const padX = 12, keyW = 22, keyH = 22, gap = 9, h = 34;
    const tw = this.promptText.width;
    const totalW = padX + keyW + gap + tw + padX;
    const left = -totalW / 2;
    const g = this.promptBg;
    g.clear();
    g.fillStyle(0x1a1206, 0.26); g.fillRoundedRect(left + 1, -h / 2 + 5, totalW, h, 13);
    g.fillStyle(0xffffff, 0.98); g.fillRoundedRect(left, -h / 2, totalW, h, 13);
    g.lineStyle(1.5, 0xdcd3c2, 1); g.strokeRoundedRect(left, -h / 2, totalW, h, 13);
    const kx = left + padX;
    g.fillStyle(0x14161a, 1); g.fillRoundedRect(kx, -keyH / 2 + 2, keyW, keyH, 5);
    g.fillStyle(0x363b42, 1); g.fillRoundedRect(kx, -keyH / 2, keyW, keyH - 2, 5);
    g.lineStyle(1, 0x50565e, 0.9); g.strokeRoundedRect(kx + 1.5, -keyH / 2 + 1.5, keyW - 3, keyH - 6, 3);
    this.promptKey.setPosition(kx + keyW / 2, -1);
    this.promptText.setPosition(kx + keyW + gap, 0);
  }
}
